import { Observable } from 'rxjs';

import { Injectable } from '@angular/core';

import { FsMapMarkersServiceProvider } from '@lib/geo/core';
import { SnLonLat } from '@lib/geo/helpers';
import { MapMarkerIcon } from '@lib/geo/map/core/marker/icon-marker-options';
import { IMapMarkerStyleOptions } from '@lib/geo/map/core/marker/marker-options';
import { FsMapPopup } from '@lib/geo/map/core/popup';
import {
  IMarkerAddEvent,
  IMarkerClickEvent,
  IMarkerDestroyEvent,
  IMarkerDoubleClickEvent,
  IMarkerDragEndEvent,
  IMarkerDragMoveEvent,
  IMarkerDragStartEvent,
  IMarkerEventUnion,
  IMarkerIconChangeEvent,
  IMarkerMouseEnterEvent,
  IMarkerMouseLeaveEvent,
  IMarkerMouseOutEvent,
  IMarkerMouseOverEvent,
  IMarkerPositionChangeEvent,
  IMarkerRemoveEvent,
  IMarkerRightClickEvent,
  IMarkerSelectionChangeEvent,
  IMarkerStyleChangeEvent,
  IMarkerTitleChangeEvent,
  IMarkerVisibilityChangeEvent,
} from '@lib/geo/map/interfaces/markers/marker-events.interface';
import { IMarkerOptions } from '@lib/geo/map/interfaces/markers/marker-options.interface';
import { IMarker as _IMarker } from '@lib/geo/map/interfaces/markers/marker.interface';
import { SnBadgeConfig } from '@lib/ui/badge';

import { GoogleMap } from '../map';
import { GoogleMarkerAdapter } from '../overlays/markers/google-marker-adapter';

type IMarker = _IMarker<GoogleMap>;

@Injectable()
export class GoogleMapMarkersService extends FsMapMarkersServiceProvider<GoogleMap> {
  public readonly geospatialProvider = 'google';
  public readonly id = 'google-map-markers-service';

  private _map: GoogleMap | null = null;

  constructor(private readonly _adapter: GoogleMarkerAdapter) {
    super();
  }

  public setMap(map: GoogleMap): void {
    this._map = map;
    // Set the map on the adapter
    this._adapter.setMap(map);
  }

  // Core marker operations
  addMarker(marker: IMarkerOptions): IMarker {
    return this._adapter.addMarker(marker);
  }

  removeMarker(marker: IMarker): void {
    this._adapter.removeMarker(marker);
  }

  updateMarker(marker: IMarker, options: Partial<IMarkerOptions>): void {
    this._adapter.updateMarker(marker, options);
  }

  // Marker lifecycle and state management
  setVisible(marker: IMarker, visible: boolean): void {
    this._adapter.setVisible(marker, visible);
  }

  setPosition(marker: IMarker, position: SnLonLat): void {
    this._adapter.setPosition(marker, position);
  }

  setIcon(marker: IMarker, icon: MapMarkerIcon): void {
    this._adapter.setIcon(marker, icon);
  }

  setStyle(marker: IMarker, style: IMapMarkerStyleOptions): void {
    this._adapter.setStyle(marker, style);
  }

  setDraggable(marker: IMarker, draggable: boolean): void {
    this._adapter.setDraggable(marker, draggable);
  }

  setRotation(marker: IMarker, rotation: number): void {
    this._adapter.setRotation(marker, rotation);
  }

  setTitle(marker: IMarker, title: string): void {
    this._adapter.setTitle(marker, title);
  }

  // Marker properties and data access
  getMarkerId(marker: IMarker): string {
    return this._adapter.getMarkerId(marker);
  }

  getMarkerPosition(marker: IMarker): SnLonLat {
    return this._adapter.getMarkerPosition(marker);
  }

  getMarkerTitle(marker: IMarker): string {
    return this._adapter.getMarkerTitle(marker);
  }

  getMarkerAttributes(marker: IMarker): Record<string, unknown> {
    return this._adapter.getMarkerAttributes(marker);
  }

  getMarkerCenter(marker: IMarker): { center: number[] } {
    return this._adapter.getMarkerCenter(marker);
  }

  // Badge management
  setBadge(marker: IMarker, badge: SnBadgeConfig): void {
    this._adapter.setBadge(marker, badge);
  }

  getBadge(marker: IMarker): SnBadgeConfig {
    return this._adapter.getBadge(marker);
  }

  createBadge(marker: IMarker, badgeClass?: string): void {
    this._adapter.createBadge(marker, badgeClass);
  }

  createSelectedBadge(marker: IMarker): void {
    this._adapter.createSelectedBadge(marker);
  }

  removeSelectedBadge(marker: IMarker): void {
    this._adapter.removeSelectedBadge(marker);
  }

  setBadgeStatus(marker: IMarker, status: string, badgePosition: Record<string, unknown>): void {
    this._adapter.setBadgeStatus(marker, status, badgePosition);
  }

  // Popup management
  setPopup(marker: IMarker, popup: FsMapPopup<GoogleMap>): void {
    this._adapter.setPopup(marker, popup);
  }

  // Event handling - Mouse and Touch Events
  onMarkerClick(marker: IMarker): Observable<IMarkerClickEvent<GoogleMap>> {
    return this._adapter.onMarkerClick(marker);
  }

  onMarkerDoubleClick(marker: IMarker): Observable<IMarkerDoubleClickEvent<GoogleMap>> {
    return this._adapter.onMarkerDoubleClick(marker);
  }

  onMarkerRightClick(marker: IMarker): Observable<IMarkerRightClickEvent<GoogleMap>> {
    return this._adapter.onMarkerRightClick(marker);
  }

  onMarkerMouseOver(marker: IMarker): Observable<IMarkerMouseOverEvent<GoogleMap>> {
    return this._adapter.onMarkerMouseOver(marker);
  }

  onMarkerMouseOut(marker: IMarker): Observable<IMarkerMouseOutEvent<GoogleMap>> {
    return this._adapter.onMarkerMouseOut(marker);
  }

  onMarkerMouseEnter(marker: IMarker): Observable<IMarkerMouseEnterEvent<GoogleMap>> {
    return this._adapter.onMarkerMouseEnter(marker);
  }

  onMarkerMouseLeave(marker: IMarker): Observable<IMarkerMouseLeaveEvent<GoogleMap>> {
    return this._adapter.onMarkerMouseLeave(marker);
  }

  // Event handling - Drag Events
  onMarkerDragStart(marker: IMarker): Observable<IMarkerDragStartEvent<GoogleMap>> {
    return this._adapter.onMarkerDragStart(marker);
  }

  onMarkerDrag(marker: IMarker): Observable<IMarkerDragMoveEvent<GoogleMap>> {
    return this._adapter.onMarkerDrag(marker);
  }

  onMarkerDragEnd(marker: IMarker): Observable<IMarkerDragEndEvent<GoogleMap>> {
    return this._adapter.onMarkerDragEnd(marker);
  }

  // Event handling - State Change Events
  onMarkerPositionChange(marker: IMarker): Observable<IMarkerPositionChangeEvent<GoogleMap>> {
    return this._adapter.onMarkerPositionChange(marker);
  }

  onMarkerTitleChange(marker: IMarker): Observable<IMarkerTitleChangeEvent<GoogleMap>> {
    return this._adapter.onMarkerTitleChange(marker);
  }

  onMarkerVisibilityChange(marker: IMarker): Observable<IMarkerVisibilityChangeEvent<GoogleMap>> {
    return this._adapter.onMarkerVisibilityChange(marker);
  }

  onMarkerSelectionChange(marker: IMarker): Observable<IMarkerSelectionChangeEvent<GoogleMap>> {
    return this._adapter.onMarkerSelectionChange(marker);
  }

  onMarkerIconChange(marker: IMarker): Observable<IMarkerIconChangeEvent<GoogleMap>> {
    return this._adapter.onMarkerIconChange(marker);
  }

  onMarkerStyleChange(marker: IMarker): Observable<IMarkerStyleChangeEvent<GoogleMap>> {
    return this._adapter.onMarkerStyleChange(marker);
  }

  // Event handling - Lifecycle Events
  onMarkerAdd(marker: IMarker): Observable<IMarkerAddEvent<GoogleMap>> {
    return this._adapter.onMarkerAdd(marker);
  }

  onMarkerRemove(marker: IMarker): Observable<IMarkerRemoveEvent<GoogleMap>> {
    return this._adapter.onMarkerRemove(marker);
  }

  onMarkerDestroy(marker: IMarker): Observable<IMarkerDestroyEvent<GoogleMap>> {
    return this._adapter.onMarkerDestroy(marker);
  }

  // Event handling - All Events (for general event listening)
  onMarkerEvents(marker: IMarker): Observable<IMarkerEventUnion<GoogleMap>> {
    return this._adapter.onMarkerEvents(marker);
  }

  // Marker query and search
  findMarkerById(id: string): IMarker | null {
    return this._adapter.findMarkerById(id);
  }

  getAllMarkers(): IMarker[] {
    return this._adapter.getAllMarkers();
  }

  getMarkersByAttribute(attribute: string, value: unknown): IMarker[] {
    return this._adapter.getMarkersByAttribute(attribute, value);
  }
}
